import React from 'react';
import { Container, Typography, Grid, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import './Relayr.scss';
import screenNetworks from './images/networks.png';
import screenDiscover from './images/discover.png';
import screenChannels from './images/channels.png';
import screenChat from './images/chat.png';

const features = [
    {
        icon: '🌐',
        title: 'Networks',
        desc: 'Join or create public and private networks around any topic — tech, gaming, business, self-improvement, and more.',
    },
    {
        icon: '💬',
        title: 'Real-Time Chat',
        desc: 'Send messages, reply to threads, and mention others instantly. Stay in the conversation no matter where you are.',
    },
    {
        icon: '📡',
        title: 'Channels',
        desc: 'Organize discussions into topic-based channels within each network. Keep conversations focused and easy to follow.',
    },
    {
        icon: '🔍',
        title: 'Discover',
        desc: 'Browse trending networks, filter by category, and find communities that match your interests.',
    },
    {
        icon: '🔔',
        title: 'Smart Notifications',
        desc: 'Get notified about what matters. Control alerts per channel so you never miss important messages.',
    },
    {
        icon: '🛡️',
        title: 'Moderation & Safety',
        desc: 'Built-in reporting, content moderation, and community guidelines to keep every network safe and welcoming.',
    },
];

const screenshots = [
    { src: screenNetworks, alt: 'Relayr networks screen showing public communities like DevHub and GameZone' },
    { src: screenDiscover, alt: 'Relayr discover screen with trending networks and category filters' },
    { src: screenChannels, alt: 'Relayr channels screen with topic-based text channels' },
    { src: screenChat, alt: 'Relayr real-time chat with messages, replies, and mentions' },
];

const Relayr = () => (
    <div className="relayr-landing">
        {/* Hero */}
        <section className="relayr-hero">
            <Container maxWidth="md">
                <span className="relayr-badge">Community Chat App</span>
                <Typography variant="h2" className="relayr-hero-title">
                    Your Communities,{' '}
                    <span className="accent">Connected</span>
                </Typography>
                <Typography className="relayr-hero-sub">
                    Relayr is a modern chat platform where you can join networks, discover communities,
                    and have real-time conversations — all in one place.
                </Typography>
                <span className="coming-soon-badge">Coming Soon to the App Store</span>
            </Container>
        </section>

        {/* Screenshots */}
        <section className="relayr-screens">
            <Container maxWidth="lg">
                <Typography variant="h5" className="screens-title">
                    See It in Action
                </Typography>
                <div className="screens-scroll">
                    {screenshots.map((screen, i) => (
                        <div className="screen-item" key={i}>
                            <img src={screen.src} alt={screen.alt} loading="lazy" />
                        </div>
                    ))}
                </div>
            </Container>
        </section>

        {/* Features */}
        <section className="relayr-features">
            <Container maxWidth="lg">
                <Typography variant="h5" className="features-title">
                    Everything You Need to Stay Connected
                </Typography>
                <Typography className="features-sub">
                    A powerful, clean chat experience built for real communities.
                </Typography>
                <Grid container spacing={3}>
                    {features.map((f, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <div className="feature-card">
                                <span className="feature-icon">{f.icon}</span>
                                <Typography variant="h6" className="feature-title">
                                    {f.title}
                                </Typography>
                                <Typography className="feature-desc">
                                    {f.desc}
                                </Typography>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </section>

        {/* CTA */}
        <section className="relayr-cta">
            <Container maxWidth="sm">
                <Typography variant="h5" className="cta-title">
                    Join the Conversation
                </Typography>
                <Typography className="cta-sub">
                    Relayr is launching soon. Be the first to connect.
                </Typography>
                <span className="coming-soon-badge">Coming Soon</span>
            </Container>
        </section>

        {/* Footer */}
        <footer className="relayr-footer">
            <Container>
                <Box>
                    <Link to="/relayr/privacy">Privacy Policy</Link>
                    <Link to="/relayr/terms">Terms of Service</Link>
                    <Link to="/relayr/support">Support</Link>
                </Box>
                <Typography className="footer-copy">
                    &copy; {new Date().getFullYear()} Evolve Labs. All rights reserved.
                </Typography>
            </Container>
        </footer>
    </div>
);

export default Relayr;
